import classNames from 'classnames';
import debounce from 'debounce-promise';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Except } from '../type-utils';
import './Autocomplete.scss';
import Input, { InputProps } from './Input';
import { Menu, MenuItem } from './Menu';

interface Props<T> extends Except<InputProps, 'innerRef' | 'onClick' | 'onFocus' | 'onBlur' | 'value'> {
  value?: string
  getItems(value: string): Promise<T[]> | T[]
  wait?: number
  renderItem(item: T, index: number): React.ReactNode
  onSelected(item: T, index: number): void
  onKeyUp?(event: React.KeyboardEvent<HTMLInputElement>): void
}

export default function Autocomplete<T>({ className, getItems: getItemsProp, wait = 300, renderItem, onSelected, onKeyUp, ...props }: Props<T>) {
  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null)
  const [isOpen, setOpen] = useState(false)
  const [data, setData] = useState({ items: new Array<T>(), isValid: true })
  const [value, setValue] = useState(props.value || '')

  const getItems = useDebouncedCallback(function (value: string) {
    lastValue.current = value
    return getItemsProp(value)
  }, wait)

  useEffect(() => {
    if (props.value !== undefined) {
      setValue(props.value)
    }
  }, [props.value])

  const lastValue = useRef(value)
  useEffect(() => {
    let mounted = true
    fetch()
    return () => { mounted = false }

    async function fetch() {
      setData(d => ({ ...d, isValid: false }))
      const items = await getItems(value)
      if (mounted && lastValue.current === value) {
        setData({ items, isValid: true })
        onShow()
      }
    }
  }, [getItems, value])

  function onShow() {
    setOpen(true)
  }

  function onClick(item: T, index: number) {
    setOpen(false)
    onSelected(item, index)
  }

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setValue(e.currentTarget.value)
    if (props.onChange) {
      props.onChange(e)
    }
  }

  return <>
    <Input {...props} autocomplete={false} value={value} onChange={onChange} className={classNames('Autocomplete', className)}
      innerRef={setInputRef} onClick={onShow} onFocus={onShow} onKeyUp={onKeyUp} />

    {data.items.length > 0 && <Menu open={isOpen} target={inputRef} onClose={() => setOpen(false)} placement='bottom' setWidth
      className={classNames('Autocomplete-menu', !data.isValid && 'stale')}>
      {data.items.map((item, index) => <MenuItem key={index} onClick={() => onClick(item, index)}>
        {renderItem(item, index)}
      </MenuItem>)}
    </Menu>}
  </>
}

export function useDebouncedCallback<Args extends any[], Ret>(callback: (...args: Args) => Ret, wait: number) {
  const lastCallback = useRef(callback)

  useEffect(() => {
    lastCallback.current = callback
  }, [callback])

  const fn = useCallback((...args: Args) => {
    return lastCallback.current(...args)
  }, [])

  return useMemo(() => debounce(fn, wait), [fn, wait])
}
